import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { VelocitySeriesPoint } from '@/hooks/useSyncedData';
import { useLocalization } from '@/hooks/useLocalization';

interface VelocityChartProps {
  data: VelocitySeriesPoint[];
}

const VelocityChart = ({ data: velocityData }: VelocityChartProps) => {
  const { t } = useLocalization();
  if (velocityData.length === 0) {
    return (
      <div className="masira-card masira-slide-up" style={{ animationDelay: '700ms' }}>
        <h3 className="font-semibold text-foreground mb-4">{t('Team Velocity')}</h3>
        <p className="text-sm text-muted-foreground py-12 text-center">{t('No completed tasks synced yet.')}</p>
      </div>
    );
  }

  const average = velocityData.reduce((sum, d) => sum + d.value, 0) / velocityData.length;

  return (
    <div className="masira-card masira-slide-up" style={{ animationDelay: '700ms' }}>
      <h3 className="font-semibold text-foreground mb-4">{t('Team Velocity')}</h3>

      <div className="h-48 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={velocityData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              width={30}
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number) => [`${value} tasks`, 'Completed']}
            />
            <Bar
              dataKey="value"
              fill="hsl(239, 84%, 67%)"
              radius={[6, 6, 0, 0]}
              maxBarSize={36}
              name="Tasks Completed"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 p-3 rounded-lg bg-secondary/30">
        <div className="flex items-center justify-between text-xs md:text-sm">
          <span className="text-muted-foreground">{t('Average velocity')}</span>
          <span className="font-semibold text-foreground">{average.toFixed(1)} tasks/week</span>
        </div>
      </div>
    </div>
  );
};

export default VelocityChart;
